import { useActionSheet } from '@expo/react-native-action-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import * as ImagePicker from 'expo-image-picker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCreateChargingRecords } from '@/api/partners/reservations/use-create-charging-records';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  compressAndResize,
  prepareAssetForUpload,
  runWithConcurrencyLimit,
  toZonedISO,
  uniqByFilename,
  UPLOAD_MIME_TYPES,
} from '@/core';
import { Button, showErrorMessage, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import FileItem from '@/ui/file-item';
import { Icon } from '@/ui/icon';
import { Paperclip } from '@/ui/icons/paperclip';
import { Plus } from '@/ui/icons/plus';
import { Loader } from '@/ui/loader';

import type { AddChargingRecordFormType } from './charging-record-form';
import ChargingRecordForm, {
  addChargingRecordFormSchema,
} from './charging-record-form';

interface AddChargingRecordModalProps {
  visible?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  id: number;
  pickupTz: string;
}

export function AddChargingRecordModal({
  visible = false,
  onClose,
  onSuccess,
  id,
  pickupTz,
}: AddChargingRecordModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [showForm, setShowForm] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { showActionSheetWithOptions } = useActionSheet();
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [images, setImages] = useState<ImagePicker.ImagePickerAsset[]>();
  const [chargingRecordForms, setChargingRecordForms] = useState<number[]>([0]);
  const [formValues, setFormValues] = useState<
    Record<number, AddChargingRecordFormType>
  >({});
  const [formValidity, setFormValidity] = useState<Record<number, boolean>>({});

  const {
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
  } = useForm<AddChargingRecordFormType>({
    resolver: zodResolver(addChargingRecordFormSchema),
  });

  const {
    mutateAsync: createChargingRecords,
    isPending: isCreatingChargingRecords,
  } = useCreateChargingRecords();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleFormChange = (index, values) => {
    setFormValues((prev) => ({ ...prev, [index]: values }));
  };

  const handleValidChange = (index, isValid) => {
    setFormValidity((prev) => ({ ...prev, [index]: isValid }));
  };

  const allValid = Object.values(formValidity).every(Boolean);

  const handleUploadPhotos = async (replaceImageFileName = null) => {
    let imgs = replaceImageFileName
      ? images?.filter(
          (img) => (img.fileName || img.name) !== replaceImageFileName
        )
      : images;

    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: UPLOAD_MIME_TYPES,
          copyToCacheDirectory: false,
          multiple: true,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map(async (asset) => {
              const response = await fetch(asset.uri);
              const blob = await response.blob();

              const file = new File([blob], asset.name || 'file.jpg', {
                type: asset.mimeType || 'image/jpeg',
              });

              return {
                file,
                name: asset.name,
                uri: asset.uri,
              };
            })
          );

          setImages(uniqByFilename([...(imgs || []), ...assets]));
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          // case takeAPhotoIdx: {
          //   const permission = await ImagePicker.getCameraPermissionsAsync();
          //   if (!permission.granted) {
          //     const permissionResult =
          //       await ImagePicker.requestCameraPermissionsAsync();

          //     if (!permissionResult.granted) {
          //       return;
          //     }
          //   }

          //   const result = await ImagePicker.launchCameraAsync({
          //     base64: true,
          //   });

          //   if (!result.canceled) {
          //     setImages(uniqByFilename([...imgs, ...result.assets]));
          //   }

          //   break;
          // }
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: UPLOAD_MIME_TYPES,
                copyToCacheDirectory: false,
                multiple: true,
              });

              if (!result.canceled) {
                const compressedResults = await runWithConcurrencyLimit(
                  result.assets.map(
                    (asset) => () => prepareAssetForUpload(asset)
                  ),
                  3 // concurrency limit
                );

                const formattedAssets = await Promise.all(
                  compressedResults.map(async (res, index) => {
                    const file: string = await FileSystem.readAsStringAsync(
                      res.uri,
                      {
                        encoding: FileSystem.EncodingType.Base64,
                      }
                    );

                    return {
                      ...result.assets[index],
                      fileName: result.assets[index].name,
                      base64: file,
                      uri: res.uri,
                    };
                  })
                );

                setImages(
                  uniqByFilename([...(imgs || []), ...formattedAssets])
                );
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: true,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              const compressedResults = await runWithConcurrencyLimit(
                result.assets.map((asset) => () => compressAndResize(asset)),
                3 // concurrency limit
              );

              const formattedResults = compressedResults.map((res, index) => ({
                ...result.assets[index],
                uri: res.uri,
                base64: res.base64,
              }));

              setImages(uniqByFilename([...(imgs || []), ...formattedResults]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  const handleSubmit = async () => {
    const chargingRecords = Object.values(formValues).map((record) => ({
      ...record,
      entryDate: toZonedISO(record.entryDate, pickupTz),
    }));

    const formattedImages =
      Platform.OS === 'web' ? images?.map((img) => img.file) : images;

    if (formattedImages!.length > 15) {
      showErrorMessage('You can only upload a maximum of 15 photos.');
      return;
    }

    try {
      await createChargingRecords({
        id: id,
        chargingRecords: chargingRecords,
        images: formattedImages,
      });

      setImages([]);
      setChargingRecordForms([0]);
      setFormValues({});
      setFormValidity({});

      onSuccess();
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>
        <View style={{ zIndex: 1 }}>
          <ScrollView
            style={{
              maxHeight: 610,
              ...(calendarOpen &&
                Platform.OS === 'web' && { overflow: 'visible' }),
            }}
            contentContainerStyle={{
              gap: 16,
              flexGrow: 1,
            }}
            showsVerticalScrollIndicator={false}
          >
            <View
              style={{
                flexDirection: 'row',
                paddingTop: 16,
                paddingLeft: 16,
              }}
            >
              <View>
                <Text type="title2">Add Charging Records</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  You can edit entries up to 72 hours after rental end
                </Text>
              </View>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setShowModal(false);
                  onClose?.();
                }}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                }}
              />
            </View>

            <View style={{ padding: 16, gap: 24, zIndex: 1 }}>
              {isCreatingChargingRecords ? (
                <Loader style={{ height: 140 }} />
              ) : showForm ? (
                <View style={{ gap: 16 }}>
                  <View style={{ gap: 16, zIndex: 1 }}>
                    <ScrollView
                      style={{
                        maxHeight: 400,
                        ...(calendarOpen &&
                          Platform.OS === 'web' && { overflow: 'visible' }),
                      }}
                      contentContainerStyle={{ gap: 32 }}
                      showsVerticalScrollIndicator={false}
                    >
                      {chargingRecordForms.map((id) => (
                        <View>
                          <ChargingRecordForm
                            key={id}
                            index={id}
                            onChange={handleFormChange}
                            onValidChange={handleValidChange}
                            pickupTz={pickupTz}
                            onCalendarOpenChange={setCalendarOpen}
                            onRemove={(index: number) => {
                              setChargingRecordForms((prev) =>
                                prev.filter((_, i) => i !== index)
                              );

                              setFormValidity((prev) => {
                                const updated = { ...prev };
                                delete updated[index];
                                return updated;
                              });
                            }}
                          />
                        </View>
                      ))}
                    </ScrollView>

                    <View style={{ alignItems: 'flex-end' }}>
                      <Pressable
                        onPress={() => {
                          setChargingRecordForms((prev) => [
                            ...prev,
                            prev.length,
                          ]);
                        }}
                      >
                        <Icon
                          type="dressed"
                          icon={<Plus />}
                          width={12}
                          height={12}
                          fillColor={theme.colors.button.secondary.default.text}
                        />
                      </Pressable>
                    </View>
                  </View>

                  <View style={{ gap: 16 }}>
                    <Text type="bodyRegular">Attachments (required)</Text>

                    <Button
                      text="Upload Files"
                      type="secondarySmall"
                      onPress={handleUploadPhotos}
                      leftIcon={
                        <Icon
                          type="naked"
                          icon={<Paperclip />}
                          width={12}
                          height={12}
                          fillColor={theme.colors.button.secondary.default.text}
                        />
                      }
                      style={{ width: 175 }}
                    />

                    <ScrollView
                      style={{ maxHeight: 125 }}
                      contentContainerStyle={{
                        gap: 16,
                        flexGrow: 1,
                      }}
                      showsVerticalScrollIndicator={false}
                      nestedScrollEnabled
                    >
                      {images?.map((image, index) => {
                        const imageFileName = image.fileName || image.name;
                        let imageUrl = image.uri;

                        return (
                          <FileItem
                            key={`${imageFileName}-${index}`}
                            imageUrl={imageUrl}
                            imageFileName={imageFileName}
                            onDelete={() => {
                              setImages(images.filter((_, i) => i !== index));
                            }}
                          />
                        );
                      })}
                    </ScrollView>
                  </View>
                </View>
              ) : (
                <Alert
                  type={AlertType.WARNING}
                  title="Manual adding charging records"
                  body={
                    "If you're opting to not use Eon's automated charging records feature you'll need to manually input all your own charging records as well as provide evidence for each of them."
                  }
                />
              )}
            </View>
          </ScrollView>
        </View>

        <View
          style={{
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: 16,
            paddingTop: 0,
            gap: 24,
          }}
        >
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Button
              text="Cancel"
              type="ghost"
              onPress={() => onClose()}
              style={styles.button}
            />

            <Button
              text={showForm ? 'Save' : 'Continue'}
              type="primary"
              onPress={() => {
                if (showForm) {
                  handleSubmit();
                } else {
                  setShowForm(true);
                }
              }}
              style={styles.button}
              disabled={
                showForm
                  ? !allValid ||
                    !images ||
                    images.length === 0 ||
                    isCreatingChargingRecords
                  : false
              }
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 700,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
}));
